import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { AddUserDialog } from './add-user-dialog';
import {
  useAccountUsers,
  useAccountUserMutations
} from '@/hooks/use-account-users';
import { useAuth } from '@/hooks/use-auth';
import { ROLES, roleLabel } from '../roles';

export function AccountUsersSection() {
  const { data: users = [], isLoading } = useAccountUsers();
  const { addUser, setRole, removeUser } = useAccountUserMutations();
  const { currentUser } = useAuth();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [addingMe, setAddingMe] = useState(false);

  const myEmail = currentUser?.email.toLowerCase();
  const isAdminHere = users.some(
    (u) => u.source === 'postgres' && u.email?.toLowerCase() === myEmail && Number(u.role) === 1
  );

  const onAddMe = async () => {
    if (!myEmail) return;
    setAddingMe(true);
    try {
      await addUser({ mode: 'existing', email: myEmail, role: 1 });
      toast.success('You are now an administrator on this account');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add you');
    } finally {
      setAddingMe(false);
    }
  };

  const onRole = async (userId: number, role: number) => {
    setBusyId(userId);
    try {
      await setRole(userId, role);
      toast.success('Role updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const onRemove = async (userId: number) => {
    setBusyId(userId);
    try {
      await removeUser(userId);
      toast.success('User removed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-end gap-2'>
        {!isLoading && !isAdminHere && (
          <Button size='sm' variant='outline' disabled={addingMe} onClick={onAddMe}>
            {addingMe ? 'Adding…' : 'Add me as administrator'}
          </Button>
        )}
        <AddUserDialog />
      </div>
      <div className='overflow-hidden rounded-xl border bg-card shadow-sm'>
        <Table>
          <TableHeader className='bg-muted/40'>
            <TableRow className='hover:bg-transparent'>
              <TableHead className='h-11 pl-4'>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className='pr-4 text-right'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Skeleton className='h-5 w-full' />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className='py-8 text-center text-sm text-muted-foreground'>
                  No users on this account yet.
                </TableCell>
              </TableRow>
            ) : (
              users.map((u, idx) => {
                const isPg = u.source === 'postgres' && u.id != null;
                return (
                  <TableRow key={u.id ?? `mongo-${u.email}-${idx}`}>
                    <TableCell className='pl-4 font-medium'>{u.name || '—'}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge variant={isPg ? 'secondary' : 'outline'}>
                        {isPg ? 'Main system' : 'Legacy'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isPg ? (
                        <Select
                          value={String(u.role ?? 0)}
                          disabled={busyId === u.id}
                          onValueChange={(v) => onRole(u.id as number, Number(v))}
                        >
                          <SelectTrigger className='w-[190px]'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r.value} value={r.value}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='text-sm text-muted-foreground'>
                          {roleLabel(u.role)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className='pr-4 text-right'>
                      {isPg && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant='ghost'
                              size='sm'
                              disabled={busyId === u.id}
                            >
                              Remove
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove this user?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {u.email} will lose access to this account. This
                                doesn&apos;t delete their Kim login.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onRemove(u.id as number)}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
